import React, { useEffect, useRef, useState } from 'react';
import { getStatusChangeDateLine, getStatusRoundLabel } from '../../utils/salesProgress';
import { KANBAN_CARD, KANBAN_CARD_DRAGGING } from './kanbanStyles';
import KanbanCardInfoRows, {
  KanbanTerminalInfoRows,
  isTerminalKanbanColumn,
  useKanbanCardSignals,
} from './KanbanCardInfoRows';

function CardMenu({ onDetail, onEdit, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-0.5 z-20 min-w-[7.5rem] py-1 bg-prodia-surface border border-prodia-border rounded-lg shadow-prodia-md"
      data-kanban-menu
    >
      <button
        type="button"
        className="w-full text-left px-3 py-1.5 text-xs text-prodia-text-primary hover:bg-prodia-background"
        onClick={(e) => {
          e.stopPropagation();
          onDetail();
          onClose();
        }}
      >
        詳細を見る
      </button>
      {onEdit && (
        <button
          type="button"
          className="w-full text-left px-3 py-1.5 text-xs text-prodia-text-primary hover:bg-prodia-background"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
            onClose();
          }}
        >
          編集
        </button>
      )}
    </div>
  );
}

function getTerminalDateMeta(columnId) {
  if (columnId === 'won') {
    return { label: '成約日', targets: ['成約'] };
  }
  return { label: '見送り日', targets: ['見送り', 'お見送り'] };
}

export default function SalesKanbanCard({
  record,
  columnId,
  primaryName,
  secondaryName,
  assignee,
  isDragging,
  formatStartMonth,
  notes,
  onDragStart,
  onDrag,
  onDragEnd,
  onOpenDetail,
  onOpenEdit,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const isTerminal = isTerminalKanbanColumn(columnId);
  const roundLabel = isTerminal ? null : getStatusRoundLabel(record.status);
  const startMonthLabel = formatStartMonth(record.start_month);

  const activeSignals = useKanbanCardSignals(record);
  const displayInterviewSignal =
    activeSignals.interviewSignal?.label === '未定' ? null : activeSignals.interviewSignal;

  const terminalMeta = getTerminalDateMeta(columnId);
  const statusDateLine = isTerminal
    ? getStatusChangeDateLine(record, terminalMeta.targets)
    : null;

  const borderClass = isTerminal ? 'border-l-prodia-border/50' : activeSignals.signals.borderClass;

  const handleClick = (e) => {
    if (e.target.closest('[data-kanban-menu]')) return;
    onOpenDetail();
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
      onClick={handleClick}
      className={`${KANBAN_CARD} ${borderClass} ${isDragging ? KANBAN_CARD_DRAGGING : ''}`}
    >
      <div
        className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 pointer-events-none text-prodia-text-muted transition-opacity"
        aria-hidden="true"
      >
        <i className="fas fa-grip-vertical text-[9px]" />
      </div>

      <div className="flex items-start justify-between gap-1 pl-2.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-1">
            <p
              className="font-semibold text-sm text-prodia-text-primary break-words leading-tight"
              title={primaryName || undefined}
            >
              {primaryName || '—'}
            </p>
            {roundLabel && (
              <span className="text-[10px] tabular-nums text-prodia-text-muted flex-shrink-0">{roundLabel}</span>
            )}
          </div>
          <p
            className="text-xs text-prodia-text-secondary break-words mt-0.5 leading-snug"
            title={secondaryName || undefined}
          >
            {secondaryName || '—'}
          </p>
        </div>

        <div className="relative flex-shrink-0" data-kanban-menu>
          <button
            type="button"
            className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded flex items-center justify-center text-prodia-text-muted hover:bg-prodia-background hover:text-prodia-text-primary transition-opacity"
            aria-label="メニュー"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
          >
            <i className="fas fa-ellipsis-h text-[10px]" />
          </button>
          {menuOpen && (
            <CardMenu onDetail={onOpenDetail} onEdit={onOpenEdit} onClose={() => setMenuOpen(false)} />
          )}
        </div>
      </div>

      <p
        className="mt-1 pl-2.5 text-[11px] text-prodia-text-secondary break-words"
        title={assignee || undefined}
      >
        {assignee || '—'}
      </p>

      {isTerminal ? (
        <KanbanTerminalInfoRows
          statusDateLabel={terminalMeta.label}
          statusDateLine={statusDateLine}
          startMonthLabel={startMonthLabel}
        />
      ) : (
        <KanbanCardInfoRows
          interviewLine={activeSignals.interviewLine}
          deadlineLine={activeSignals.deadlineLine}
          startMonthLabel={startMonthLabel}
          interviewSignal={displayInterviewSignal}
          deadlineSignal={activeSignals.deadlineSignal}
        />
      )}

      {notes && (
        <p
          className="mt-1 pl-2.5 pt-1 border-t border-prodia-border text-[9px] text-prodia-text-muted truncate"
          title={notes}
        >
          {notes}
        </p>
      )}
    </div>
  );
}
